import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@mds/shared/ui/shadcn/empty';

import { MediaCardRow } from '@/components/card/media-card';
import SpaceCard from '@/components/card/space-card';
import FilterDropdown from '@/components/form/filter-dropdown';
import PageHeader from '@/components/heading/page-header';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '@/components/states';

import { spaceGroupOf, spaceGroupOptions } from '@/features/spaces/space-types';
import { spaceQueries } from '@/features/spaces/spaces.queries';

/** See the note in home.tsx — this wants to be a `<Section>` component. */
const band = 'mx-auto max-w-page px-gutter pt-section';

/** "" is every kind. Not a value in the data — an affordance of the filter. */
const ALL = '';

/**
 * The filter bar is hidden for now. Its code stays wired up so it comes back
 * by setting this to true; with it off, `group` stays ALL and every space shows.
 */
const SHOW_FILTERS = false;

export default function SpacesPage() {
  const spaces = useQuery(spaceQueries.list());
  const [group, setGroup] = useState<string>(ALL);

  const all = spaces.data ?? [];
  const shown = group === ALL ? all : all.filter((space) => spaceGroupOf(space.type) === group);

  // Filtered here rather than on the server: the list is small and already
  // loaded, and the counts need every space anyway.
  const options = [
    { value: ALL, label: 'All spaces', count: all.length },
    ...spaceGroupOptions(all).map((option) => ({
      value: option.id as string,
      label: option.label,
      count: option.count,
    })),
  ];

  return (
    <>
      <section className={band}>
        <PageHeader
          title="Spaces"
          description="A desk for the day, a meeting room for the hour, or the hall for your event."
        />
      </section>

      {spaces.isPending ? (
        <section className="mx-auto max-w-page px-gutter pt-block">
          <LoadingBlock />
        </section>
      ) : spaces.isError ? (
        <section className="mx-auto max-w-page px-gutter pt-block">
          <ErrorBlock
            title="We couldn't load the spaces"
            error={spaces.error}
            onRetry={() => void spaces.refetch()}
          />
        </section>
      ) : all.length === 0 ? (
        <section className="mx-auto max-w-page px-gutter pt-block">
          <EmptyBlock title="No spaces to show yet" hint="Check back soon." />
        </section>
      ) : (
        <>
          {/* Hidden for now: see SHOW_FILTERS. */}
          {SHOW_FILTERS && (
            <section className="mx-auto max-w-page px-gutter pt-block" aria-label="Filter spaces">
              <div className="flex flex-wrap items-end gap-4">
                <FilterDropdown label="Kind" options={options} value={group} onChange={setGroup} />

                <p className="pb-2.5 text-sm text-ink-muted">
                  Showing {shown.length} of {all.length}
                </p>
              </div>
            </section>
          )}

          <section className="mx-auto max-w-page px-gutter pt-block">
            {shown.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>No spaces of that kind</EmptyTitle>
                  <EmptyDescription>
                    Nothing here matches that filter yet. Clear it to see them all.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <MediaCardRow>
                {shown.map((space) => (
                  <SpaceCard key={space.id} space={space} />
                ))}
              </MediaCardRow>
            )}
          </section>
        </>
      )}
    </>
  );
}
